import React from 'react';
import {
  Box,
  Heading,
  Flex,
  Spinner,
  useToast,
  Text,
  Badge,
  Image,
  Icon,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
} from '@chakra-ui/react';
import {
  FiMail,
  FiPhone,
  FiMapPin,
  FiGlobe,
  FiUser,
  FiUsers,
  FiDollarSign,
  FiBookOpen,
  FiLink,
} from 'react-icons/fi';
import xhr from 'xhr';

function InfoRow({ icon, label, value }) {
  if (!value || value === '-') return null;
  return (
    <Flex align="center" gap={3} py={2}>
      <Flex
        w="36px"
        h="36px"
        borderRadius="lg"
        bg="gray.50"
        align="center"
        justify="center"
        flexShrink={0}
      >
        <Icon as={icon} color="gray.500" boxSize={4} />
      </Flex>
      <Box>
        <Text fontSize="xs" color="gray.500" lineHeight="1">
          {label}
        </Text>
        <Text fontSize="sm" fontWeight="medium">
          {value}
        </Text>
      </Box>
    </Flex>
  );
}

function StatCard({ icon, label, value, color }) {
  return (
    <Box bg="white" p={5} borderRadius="xl" border="1px solid" borderColor="gray.100">
      <Flex align="center" gap={3}>
        <Flex
          w="40px"
          h="40px"
          borderRadius="lg"
          bg={`${color}.50`}
          align="center"
          justify="center"
          flexShrink={0}
        >
          <Icon as={icon} boxSize={4} color={`${color}.500`} />
        </Flex>
        <Stat>
          <StatLabel fontSize="xs" color="gray.500">{label}</StatLabel>
          <StatNumber fontSize="xl">{value}</StatNumber>
        </Stat>
      </Flex>
    </Box>
  );
}

export default function SchoolMiInstitucion() {
  const toast = useToast();

  const [loading, setLoading] = React.useState(true);
  const [school, setSchool] = React.useState(null);
  const [schoolId, setSchoolId] = React.useState(null);
  const [stats, setStats] = React.useState({ students: 0, payments: 0 });
  const [programs, setPrograms] = React.useState([]);

  React.useEffect(() => {
    const init = async () => {
      try {
        const userRes = await xhr('/users/me?populate=school');
        const userSchool = userRes.data?.school;

        if (!userSchool?.id) {
          toast({
            title: 'No se encontro una institucion vinculada a tu cuenta',
            status: 'error',
            position: 'bottom',
            duration: 5000,
            isClosable: true,
          });
          setLoading(false);
          return;
        }

        setSchoolId(userSchool.id);

        const schoolRes = await xhr(`/schools/${userSchool.id}?populate=*`);
        const data = schoolRes.data.data;
        const attrs = data.attributes || data;
        setSchool(attrs);

        // Fetch stats, programs, and admin users in parallel
        const [studentsRes, paymentsRes, programsRes] = await Promise.all([
          xhr(
            `/students?filters[schoolFrom][id][$eq]=${userSchool.id}&filters[user][id][$notNull]=true&pagination[pageSize]=1&pagination[withCount]=true`
          ),
          xhr(
            `/payment-schools?filters[school][id][$eq]=${userSchool.id}&pagination[pageSize]=1&pagination[withCount]=true`
          ),
          xhr(
            `/school-programs?filters[school][id][$eq]=${userSchool.id}&filters[active][$eq]=true&populate=program_type,course,grade,technical_career&pagination[pageSize]=100`
          ).catch(() => ({ data: { data: [] } })),
        ]);

        setStats({
          students: studentsRes.data.meta?.pagination?.total || 0,
          payments: paymentsRes.data.meta?.pagination?.total || 0,
        });

        const programData = programsRes.data?.data || [];
        const mapped = programData.map((p) => {
          const pAttrs = p.attributes || p;
          const programType = pAttrs.program_type?.data?.attributes || pAttrs.program_type;
          const course = pAttrs.course?.data?.attributes || pAttrs.course;
          const grade = pAttrs.grade?.data?.attributes || pAttrs.grade;
          const career = pAttrs.technical_career?.data?.attributes || pAttrs.technical_career;
          return {
            id: p.id,
            typeName: programType?.name || 'Otros',
            typeSlug: programType?.slug || '',
            programName: course?.name || grade?.name || career?.name || '',
          };
        });

        // Group by program type
        const grouped = {};
        mapped.forEach((p) => {
          if (!grouped[p.typeName]) {
            grouped[p.typeName] = [];
          }
          grouped[p.typeName].push(p);
        });
        setPrograms(grouped);

      } catch (error) {
        console.error('Error loading school:', error);
        toast({
          title: 'Error al cargar datos de la institucion',
          status: 'error',
          position: 'bottom',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  if (loading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="lg" />
      </Box>
    );
  }

  if (!school) {
    return (
      <Box p={8} textAlign="center">
        <Text color="gray.500">No se encontro una institucion vinculada a tu cuenta.</Text>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with logo */}
      <Box
        bg="white"
        borderRadius="xl"
        border="1px solid"
        borderColor="gray.100"
        overflow="hidden"
        mb={6}
      >
        <Box bg="purple.50" h="80px" />
        <Flex px={6} mt="-40px" pb={5} align="flex-end" gap={4}>
          {school.logo_url ? (
            <Image
              src={school.logo_url}
              alt={school.name}
              w="80px"
              h="80px"
              borderRadius="xl"
              border="4px solid white"
              bg="white"
              objectFit="contain"
              boxShadow="sm"
            />
          ) : (
            <Flex
              w="80px"
              h="80px"
              borderRadius="xl"
              border="4px solid white"
              bg="purple.500"
              align="center"
              justify="center"
              boxShadow="sm"
            >
              <Text fontSize="2xl" fontWeight="bold" color="white">
                {school.name?.[0]?.toUpperCase() || 'E'}
              </Text>
            </Flex>
          )}
          <Box pb={1}>
            <Flex align="center" gap={2}>
              <Heading size="lg">{school.name}</Heading>
              <Badge colorScheme={school.active ? 'green' : 'red'} borderRadius="full" px={2}>
                {school.active ? 'Activo' : 'Inactivo'}
              </Badge>
            </Flex>
            {school.domain && (
              <Text fontSize="sm" color="gray.500">
                {school.domain}.eduplataforma.co
              </Text>
            )}
          </Box>
        </Flex>
      </Box>

      {/* Stats */}
      <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={4} mb={6}>
        <StatCard icon={FiUsers} label="Estudiantes" value={stats.students} color="blue" />
        <StatCard icon={FiDollarSign} label="Pagos registrados" value={stats.payments} color="green" />
        <StatCard icon={FiBookOpen} label="Programas activos" value={Object.values(programs).reduce((sum, arr) => sum + arr.length, 0)} color="purple" />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        {/* Contact Info */}
        <Box
          bg="white"
          p={6}
          borderRadius="xl"
          border="1px solid"
          borderColor="gray.100"
        >
          <Heading size="sm" mb={4}>
            Informacion de contacto
          </Heading>
          <Flex direction="column" gap={1}>
            <InfoRow icon={FiUser} label="Persona de contacto" value={school.contact_name} />
            <InfoRow icon={FiMail} label="Email" value={school.contact_email} />
            <InfoRow icon={FiPhone} label="Telefono" value={school.cellphone} />
          </Flex>
        </Box>

        {/* General Info */}
        <Box
          bg="white"
          p={6}
          borderRadius="xl"
          border="1px solid"
          borderColor="gray.100"
        >
          <Heading size="sm" mb={4}>
            Informacion general
          </Heading>
          <Flex direction="column" gap={1}>
            <InfoRow icon={FiLink} label="Subdominio" value={school.domain ? `${school.domain}.eduplataforma.co` : null} />
            <InfoRow icon={FiMapPin} label="Ciudad" value={school.city} />
            <InfoRow icon={FiGlobe} label="Pais" value={school.country} />
          </Flex>
          {!school.domain && !school.city && !school.country && (
            <Flex align="center" justify="center" py={6} color="gray.400">
              <Icon as={FiLink} mr={2} />
              <Text fontSize="sm">Sin informacion adicional registrada</Text>
            </Flex>
          )}
        </Box>

        {/* Programs */}
        <Box
          bg="white"
          p={6}
          borderRadius="xl"
          border="1px solid"
          borderColor="gray.100"
          gridColumn={{ lg: '1 / -1' }}
        >
          <Flex align="center" gap={2} mb={5}>
            <Heading size="sm">Programas habilitados</Heading>
            <Badge borderRadius="full" colorScheme="gray" fontSize="xs">
              {Object.values(programs).reduce((sum, arr) => sum + arr.length, 0)}
            </Badge>
          </Flex>
          {Object.keys(programs).length > 0 ? (
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {Object.entries(programs).map(([typeName, items]) => (
                <Box
                  key={typeName}
                  border="1px solid"
                  borderColor="gray.100"
                  borderRadius="lg"
                  overflow="hidden"
                >
                  <Flex
                    bg="gray.50"
                    px={4}
                    py={3}
                    align="center"
                    justify="space-between"
                  >
                    <Flex align="center" gap={2}>
                      <Icon as={FiBookOpen} color="purple.500" boxSize={4} />
                      <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                        {typeName}
                      </Text>
                    </Flex>
                    <Badge borderRadius="full" colorScheme="purple" variant="subtle" fontSize="xs">
                      {items.length}
                    </Badge>
                  </Flex>
                  <Box px={4} py={3}>
                    <Flex direction="column" gap={2}>
                      {items.map((p) => (
                        <Flex key={p.id} align="center" gap={2}>
                          <Box w="6px" h="6px" borderRadius="full" bg="purple.300" flexShrink={0} />
                          <Text fontSize="sm" color="gray.600">
                            {p.programName || typeName}
                          </Text>
                        </Flex>
                      ))}
                    </Flex>
                  </Box>
                </Box>
              ))}
            </SimpleGrid>
          ) : (
            <Flex align="center" justify="center" py={6} color="gray.400">
              <Icon as={FiBookOpen} mr={2} />
              <Text fontSize="sm">No hay programas asignados a esta institucion</Text>
            </Flex>
          )}
        </Box>
      </SimpleGrid>
    </Box>
  );
}
